import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { customersAPI } from '../services/api';
import { Users, Search, Filter, Plus, Mail, Phone, MapPin, Eye, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Customers = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);

  const apiParams = {
    page,
    limit,
    search: searchTerm || undefined,
  };

  const { data: customersData, isLoading, error } = useQuery(
    ['customers', apiParams],
    () => customersAPI.getAll(apiParams),
    {
      keepPreviousData: true,
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  React.useEffect(() => {
    if (customersData?.pagination) {
      setTotalPages(customersData.pagination.totalPages);
    }
  }, [customersData]);

  React.useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600">Error loading customers</div>;

  const customers = customersData?.customers || [];
  const total = customersData?.pagination?.total || 0;

  const handleCustomerClick = (customerId) => {
    navigate(`/customers/${customerId}`);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Summary - moved to top */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Customer Summary</h3>
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-bold text-gray-900">{customers.length}</p>
            <p className="text-xs sm:text-sm text-gray-600">Total Customers</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-bold text-blue-600">{customers.filter(c => c.shipment_count > 0).length}</p>
            <p className="text-xs sm:text-sm text-gray-600">Active Customers</p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage customer information and relationships</p>
        </div>
        <button 
          className="flex items-center justify-center px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          onClick={() => navigate('/customers/new')}
        >
          <Plus className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline">Add Customer</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button className="flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm">
              <Filter className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">More Filters</span>
              <span className="sm:hidden">Filters</span>
            </button>
          </div>
        </div>
      </div>

      {/* Customers Table/List View */}
      {customers.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer Since</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Shipments</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleCustomerClick(customer.id)}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 text-blue-600 mr-2" />
                      <span className="font-medium text-gray-900">{customer.full_name}</span>
                    </div>
                    <div className="text-xs text-gray-500">#{customer.id}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-700">{customer.phone_number}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-700">{new Date(customer.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-700">{customer.shipment_count || 0}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:underline mr-2"
                      onClick={e => { e.stopPropagation(); handleCustomerClick(customer.id); }}
                    >
                      <Eye className="h-4 w-4 mr-1 inline" /> View
                    </button>
                    <button
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:underline"
                      onClick={e => { e.stopPropagation(); navigate(`/customers/${customer.id}/edit`); }}
                    >
                      <Edit className="h-4 w-4 mr-1 inline" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center px-4 py-3 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * limit + 1} - {Math.min(page * limit, total)} of {total} customers
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="p-2 rounded-full border border-gray-300 disabled:opacity-50"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-medium">Page {page} of {totalPages}</span>
                <button
                  className="p-2 rounded-full border border-gray-300 disabled:opacity-50"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
          <Users className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No customers found</h3>
          <p className="mt-1 text-xs sm:text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by adding a new customer.'}
          </p>
          {!searchTerm && (
            <div className="mt-4 sm:mt-6">
              <button
                onClick={() => navigate('/customers/new')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Customer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Customers; 